import { IconName } from '@topcon/tuicl';
import { TeContainerSlim, TeIcon, TeText } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import SelectableItem from '../SelectableItem';
import { AgreementType } from '../../types/customer-modal.types';

export interface SelectableAgreementOptionDetails {
  title: string;
  textColor: string;
  iconName: IconName;
  iconColor: string;
  iconOutline?: boolean;
}

interface SelectableAgreementTypeCardProps {
  title: string;
  onClick?: () => void;
  agreementType: AgreementType;
  isActive: boolean;
  disabled?: boolean;
  badgeLabel?: string;
}

const PERMISSION_KEYS = [
  'serviceAgreement.agreementReview.viewLicenseInventory',
  'serviceAgreement.agreementReview.viewDeviceInventory',
  'serviceAgreement.agreementReview.productRegistration',
  'serviceAgreement.agreementReview.productActivation',
  'serviceAgreement.agreementReview.subscriptionManagement',
  'serviceAgreement.agreementReview.userAdministration',
  'serviceAgreement.agreementReview.deviceAdministration',
  'serviceAgreement.agreementReview.assetAdministration',
  'serviceAgreement.agreementReview.applicationAccess',
  'serviceAgreement.agreementReview.applicationAdministration',
  'serviceAgreement.agreementReview.applicationDataManagement',
  'serviceAgreement.agreementReview.operationalDataAdministration',
] as const;

/** How many leading rows are granted. The rest render with a cross. */
const GRANTED_PERMISSION_COUNT: Record<AgreementType, number> = {
  [AgreementType.FullService]: PERMISSION_KEYS.length,
  [AgreementType.LimitedService]: 5,
};

function SelectableAgreementTypeCard({
  title,
  agreementType,
  isActive,
  onClick,
  disabled,
  badgeLabel,
}: SelectableAgreementTypeCardProps) {
  const { t } = useTranslation();
  const grantedCount = GRANTED_PERMISSION_COUNT[agreementType];

  const optionDetails: SelectableAgreementOptionDetails[] = PERMISSION_KEYS.map((key, index) => {
    const granted = index < grantedCount;
    return {
      title: t(key),
      iconName: granted ? 'check_circle_filled' : 'cancel',
      textColor: granted ? 'text.primary' : 'text.secondary',
      iconColor:
        isActive && granted ? 'icon.info.primary' : !granted ? 'icon.secondary' : 'icon.primary',
      iconOutline: !granted,
    };
  });

  return (
    <SelectableItem
      active={isActive}
      title={title}
      onClick={() => onClick?.()}
      padding="l"
      showActiveLabel={isActive}
      titleTextSize="xl"
      disabled={disabled}
      badgeLabel={badgeLabel}
    >
      <TeContainerSlim column fill gap="xs" padding={['s', 0]} width={280}>
        {optionDetails.map((option) => (
          <TeContainerSlim fill grow gap="xs" key={option.title}>
            <TeIcon
              size="l"
              name={option.iconName}
              color={option.iconColor}
              outline={option.iconOutline}
            />
            <TeContainerSlim column gap="xs" grow fill padding={['xs2', 0]}>
              <TeText weight={700} size={14} color={option.textColor}>
                {option.title}
              </TeText>
            </TeContainerSlim>
          </TeContainerSlim>
        ))}
      </TeContainerSlim>
    </SelectableItem>
  );
}

export default SelectableAgreementTypeCard;
